import { useState, useEffect } from "react";
import { Mail, Sparkles, Loader2, Send } from "lucide-react";
import { Button } from "./ui/button";

export type EmailTemplateType =
  | "candidate_outreach"
  | "firm_outreach"
  | "firm_skm"
  | "resume_permission"
  | "interview_coordination"
  | "submission";

export interface EmailContext {
  candidate?: Record<string, unknown>;
  job?: Record<string, unknown>;
  target?: Record<string, unknown>;
  interview?: Record<string, unknown>;
  narrative_mode?: boolean;
}

interface Props {
  templateTypes?: EmailTemplateType[];
  context: EmailContext;
  aiAssist: boolean;
  defaultType?: EmailTemplateType;
  recipientEmail?: string;
}

const TEMPLATE_LABELS: Record<EmailTemplateType, string> = {
  candidate_outreach:     "Candidate Outreach",
  firm_outreach:          "Firm Outreach",
  firm_skm:               "Skill Marketing",
  resume_permission:      "Resume Permission",
  interview_coordination: "Interview Coordination",
  submission:             "Submission",
};

const TEMPLATE_HINTS: Partial<Record<EmailTemplateType, string>> = {
  firm_outreach:          "Email to the firm about this role — inquire about the opening or introduce a candidate.",
  firm_skm:               "Skill-markets a candidate to the firm without tying them to a specific role.",
  submission:             "Formally submits a candidate's materials for this role.",
  candidate_outreach:     "Cold outreach to a candidate about a lateral opportunity.",
  resume_permission:      "Asks the candidate's permission before submitting their resume to a firm.",
  interview_coordination: "Coordinates interview scheduling between the candidate and the firm.",
};

const ALL_TYPES: EmailTemplateType[] = [
  "candidate_outreach", "firm_outreach", "firm_skm",
  "resume_permission", "interview_coordination", "submission",
];

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
}

export default function EmailComposer({ templateTypes, context, aiAssist, defaultType, recipientEmail }: Props) {
  const types = templateTypes ?? ALL_TYPES;
  const [templateType, setTemplateType] = useState<EmailTemplateType>(defaultType ?? types[0]);
  const [subject, setSubject]           = useState("");
  const [body, setBody]                 = useState("");
  const [loading, setLoading]           = useState(false);
  const [polishing, setPolishing]       = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [showConfirm, setShowConfirm]       = useState(false);
  const [toEmail, setToEmail]               = useState(recipientEmail ?? "");
  const [sending, setSending]               = useState(false);
  const [sent, setSent]                     = useState(false);
  const [sequences, setSequences]           = useState<Array<{ id: string; name: string }>>([]);
  const [selectedSequenceId, setSelectedSequenceId] = useState("");
  const [showMixmaxPicker, setShowMixmaxPicker]     = useState(false);
  const [addingToMixmax, setAddingToMixmax]         = useState(false);
  const [addedToMixmax, setAddedToMixmax]           = useState(false);

  useEffect(() => {
    fetch("/api/emails/sequences", { headers: { "X-CSRF-Token": csrfToken() } })
      .then((r) => r.json())
      .then((data) => setSequences(data.sequences ?? []))
      .catch(() => {});
  }, []);

  async function generateDraft() {
    setLoading(true);
    setError(null);
    try {
      const result = await callDraftApi({ ai_polish: false });
      setSubject(result.subject);
      setBody(result.body);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Draft generation failed");
    } finally {
      setLoading(false);
    }
  }

  async function polishWithAi() {
    setPolishing(true);
    setError(null);
    try {
      const result = await callDraftApi({ ai_polish: true });
      setSubject(result.subject);
      setBody(result.body);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "AI polish failed");
    } finally {
      setPolishing(false);
    }
  }

  async function sendViaGmail() {
    setSending(true);
    setError(null);
    try {
      const res = await fetch("/api/emails/send", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ to: toEmail, subject, body }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error ?? "Send failed");
      setSent(true);
      setShowConfirm(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Send failed");
      setShowConfirm(false);
    } finally {
      setSending(false);
    }
  }

  async function addToMixmaxSequence() {
    setAddingToMixmax(true);
    setError(null);
    const candidateEmail = recipientEmail ?? (context.candidate?.email as string | undefined) ?? "";
    try {
      const res = await fetch("/api/emails/add_to_sequence", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({
          sequence_id:   selectedSequenceId || "",
          email:         candidateEmail,
          first_name:    (context.candidate?.first_name as string | undefined) ?? "",
          last_name:     (context.candidate?.last_name as string | undefined) ?? "",
          template_type: templateType,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error ?? "Failed to add to sequence");
      setAddedToMixmax(true);
      setShowMixmaxPicker(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to add to sequence");
      setShowMixmaxPicker(false);
    } finally {
      setAddingToMixmax(false);
    }
  }

  async function callDraftApi({ ai_polish }: { ai_polish: boolean }): Promise<{ subject: string; body: string }> {
    const payload: Record<string, unknown> = {
      template_type: templateType,
      ai_polish:     ai_polish.toString(),
      narrative_mode: context.narrative_mode?.toString() ?? "false",
    };
    if (context.candidate)  payload.candidate  = context.candidate;
    if (context.job)        payload.job        = context.job;
    if (context.target)     payload.target     = context.target;
    if (context.interview)  payload.interview  = context.interview;

    const res = await fetch("/api/emails/draft", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok || data.error) throw new Error(data.error ?? "API error");
    return data as { subject: string; body: string };
  }

  const hasDraft = subject || body;

  return (
    <div className="space-y-3">
      {/* Template type selector */}
      <div className="flex items-center gap-3">
        <select
          value={templateType}
          onChange={(e) => { setTemplateType(e.target.value as EmailTemplateType); setSubject(""); setBody(""); }}
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          {types.map((t) => (
            <option key={t} value={t}>{TEMPLATE_LABELS[t]}</option>
          ))}
        </select>

        <button
          onClick={generateDraft}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
          Generate Draft
        </button>

        {hasDraft && aiAssist && (
          <button
            onClick={polishWithAi}
            disabled={polishing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-700 text-white rounded-md hover:bg-purple-800 disabled:opacity-50 transition-colors"
          >
            {polishing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            Polish with AI
          </button>
        )}
      </div>

      {TEMPLATE_HINTS[templateType] && (
        <p className="text-xs text-gray-400 -mt-1">{TEMPLATE_HINTS[templateType]}</p>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      {/* Subject */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject line..."
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      {/* Body */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Body</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          placeholder="Email body..."
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      {/* Send actions */}
      {hasDraft && (
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={() => { setToEmail(recipientEmail ?? ""); setShowConfirm(true); }}
            disabled={sent || sending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-green-600 text-green-700 rounded-md hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send size={13} />
            {sent ? "Sent" : "Send via Gmail"}
          </button>
          <button
            onClick={() => { setSelectedSequenceId(""); setShowMixmaxPicker(true); }}
            disabled={addedToMixmax || addingToMixmax}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-blue-600 text-blue-700 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {addedToMixmax ? "Added to Sequence" : "Add to MixMax Sequence"}
          </button>
        </div>
      )}

      {/* Gmail send confirmation dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Send via Gmail</h3>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="recipient@example.com"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <p className="text-sm text-gray-600">
              Subject: <span className="font-medium">{subject}</span>
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>
              <button
                onClick={sendViaGmail}
                disabled={sending || !toEmail.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-green-700 text-white rounded-md hover:bg-green-800 disabled:opacity-50 transition-colors"
              >
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {sending ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* MixMax sequence picker dialog */}
      {showMixmaxPicker && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Add to MixMax Sequence</h3>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sequence</label>
              <select
                value={selectedSequenceId}
                onChange={(e) => setSelectedSequenceId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Create new sequence</option>
                {sequences.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-gray-500">
              Recipient: {recipientEmail ?? (context.candidate?.email as string | undefined) ?? "—"}
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setShowMixmaxPicker(false)}>
                Cancel
              </Button>
              <button
                onClick={addToMixmaxSequence}
                disabled={addingToMixmax}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-700 text-white rounded-md hover:bg-blue-800 disabled:opacity-50 transition-colors"
              >
                {addingToMixmax ? <Loader2 size={13} className="animate-spin" /> : null}
                {addingToMixmax ? "Adding..." : "Add to Sequence"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
